import assert from 'assert';
import find from 'lodash/find';
import pickBy from 'lodash/pickBy';

import { isRef, split, join } from '../../../muster-utils';
import {
  serialize,
  isMap,
  isCallOrSet,
  isFragment,
  isDeferred,
  isLeafNode,
  isList,
  isDynamicList,
  joinPathSegments,
  parseStaticRange,
  when,
  otherwise,
} from '../utils';

function isFlatNode(node) {
  return (
    node.$node === undefined || node.$node === true ||
    (isDeferred(node.$node) && node.$node.$node === true)
  );
}

function validateList(node, key) {
  return (memo) => {
    assert(
      node.$range !== undefined,
      `List node for field \`${key}\` has no \`$range\` definition.\n` +
      'Expected type `$range: { from: number, to: number } ' +
      '| { offset: number, length: number }`.'
    );

    const range = node.$range;
    assert(
      ((
        (typeof range.from === 'number' || isRef(range.from)) &&
        (typeof range.to === 'number' || isRef(range.to))
      ) || (
        (typeof range.offset === 'number' || isRef(range.offset)) &&
        (typeof range.length === 'number' || isRef(range.length))
      )),
      (
        `List node for field \`${key}\` has an invalid \`$range\` definition.\n` +
        'Expected type `$range: { from: number | ref, to: number | ref } ' +
        '| { offset: number | ref, length: number | ref }`, ' +
        `instead received \`${serialize(range)}\`.`
      )
    );

    return memo;
  };
}

function validateMap(node, key) {
  return (memo) => {
    assert(
      node.$keys !== undefined,
      `Map node for field \`${key}\` has no \`$keys\` definition.\n` +
      'Expected type `$keys: Array<string>`.'
    );

    const keys = node.$keys;
    assert(
      keys.length !== 0,
      (
        `Map node for field \`${key}\` has an invalid \`$keys\` definition.\n` +
        'Expected type `$keys: Array<string>`, ' +
        `instead received \`${serialize(keys)}\`.`
      )
    );

    return memo;
  };
}

function validateAction(node, key) {
  return (memo) => {
    const keys = Object.keys(node);
    assert(
      keys.length === 1,
      `Action definition for key \`${key}\` can have only one action type.`
    );

    return memo;
  };
}

function addPrefixedPath(prefixedPath) {
  return (memo) => memo.add(prefixedPath);
}

function addListPaths(node, prefixedPath) {
  return (memo) => {
    if (isDynamicList(node)) {
      return addDynamicListPaths(node, prefixedPath)(memo);
    }
    return addStaticListPaths(node, prefixedPath)(memo);
  };
}

function addStaticListPaths(node, prefixedPath) {
  return (memo) => {
    const { from, to } = parseStaticRange(node.$range);

    if (to < from) {
      // When trying to reference an empty list the `to` value will be less than
      // the `from` value and this causes issues with the falcor path syntax.
      // So to avoid the issue entirely: don't request the list at
      // all and always return an empty list to the component.
      return memo;
    }

    const newParentPath = `${prefixedPath}[${from}..${to}]`;

    if (isFlatNode(node)) {
      return new Set([
        ...memo,
        newParentPath,
      ]);
    }

    return new Set([
      ...memo,
      ...getQueryPaths(node.$node, newParentPath),
    ]);
  };
}

function addDynamicListPaths(node, prefixedPath) {
  return (memo) => {
    const dynamicKeys = Object.keys(pickBy(node.$range, isRef));
    const dependencies = dynamicKeys.reduce((acc, key) => {
      acc.push(node.$range[key].value);
      return acc;
    }, []);
    const staticValues = pickBy(node.$range, (value) => !isRef(value));

    const dependentPath = {
      dependencies,
      resolve: (resolvedPathValues) => {
        const resolvedDependencies = dynamicKeys.reduce((acc, key) => {
          const path = node.$range[key].value;
          const { value } = find(resolvedPathValues, { path });
          acc[key] = value; // eslint-disable-line no-param-reassign
          return acc;
        }, {});

        const { from, to } = parseStaticRange({
          ...staticValues,
          ...resolvedDependencies,
        });

        const paths = split(prefixedPath);
        const head = paths.slice(0, -1);
        const tail = paths[paths.length - 1];

        const newNode = {
          [tail]: {
            ...node,
            $range: { from, to },
          },
        };

        return getQueryPaths(newNode, join(head));
      },
    };

    return new Set([
      ...memo,
      dependentPath,
    ]);
  };
}

function addMapPaths(node, prefixedPath) {
  return (memo) => {
    const newParentPath = `${prefixedPath}[${node.$keys.map((k) => `"${k}"`).join(',')}]`;

    if (isFlatNode(node)) {
      return new Set([
        ...memo,
        newParentPath,
      ]);
    }

    return new Set([
      ...memo,
      ...getQueryPaths(
        node.$node,
        newParentPath
      ),
    ]);
  };
}

function addFragmentPaths(node, parentPath) {
  if (node.$fields && isList(node.$fields)) {
    return addListPaths(node.$fields, parentPath);
  }
  if (node.$fields && isMap(node.$fields)) {
    return addMapPaths(node.$fields, parentPath);
  }
  return (memo) =>
    new Set([
      ...memo,
      ...getQueryPaths(node.$fields, parentPath),
    ]);
}

function addDeferredPaths(node, key, parentPath) {
  return (memo) =>
    new Set([
      ...memo,
      ...getQueryPaths({ [key]: node.$node }, parentPath),
    ]);
}

function addChildPaths(node, prefixedPath) {
  return (memo) =>
    new Set([
      ...memo,
      ...getQueryPaths(node, prefixedPath),
    ]);
}

export default function getQueryPaths(queries, parentPath = null) {
  if (isDeferred(queries)) {
    return getQueryPaths(queries.$node, parentPath);
  }

  return Object.keys(queries).reduce((memo, key) => {
    const node = queries[key];
    const prefixedPath = joinPathSegments(parentPath, key);

    return find([
      when(isLeafNode, addPrefixedPath(prefixedPath)),
      when(isCallOrSet, validateAction(node, key)),
      when(isList, validateList(node, key), addListPaths(node, prefixedPath)),
      when(isMap, validateMap(node, key), addMapPaths(node, prefixedPath)),
      when(isFragment, addFragmentPaths(node, parentPath)),
      when(isDeferred, addDeferredPaths(node, key, parentPath)),
      otherwise(addChildPaths(node, prefixedPath)),
    ], (transformer) => transformer.predicate(node)).func(memo);
  }, new Set());
}
